import { useEffect, useRef, useState } from "react";
import { getPublicBookingConfirmationPage, isApiError, manageSurface } from "@v0y4ge/api-client";
import type { ManageViewDto, PublicConfirmationPageResponse } from "@v0y4ge/api-contracts";
import { ConfirmationPageContent } from "../../manage/DesignedConfirmationPage";
import { answerString } from "../../manage/sections";
import { manageIntl, pageLocale } from "../../manage/strings";
import { timeFormatter } from "../../manage/format";
import { toPutInput, type CPDoc } from "./confirmation-pages-api";

// LOCAL PATCH (platform side, 2026-09-09, JAI-1577): preview the draft through
// the customer renderer. Only booking reads/session exchange; never publish.
export default function ConfirmationPagePreview({ doc, onClose }: { doc: CPDoc; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const generation = useRef(0);
  const [bookingId, setBookingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ res: PublicConfirmationPageResponse; view: ManageViewDto } | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
    return () => { generation.current++; };
  }, []);

  async function load() {
    const current = ++generation.current;
    setLoaded(null);
    setError(null);
    const id = bookingId.trim();
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      setError("Enter the booking ID from the customer's confirmation link.");
      return;
    }
    setLoading(true);
    try {
      const [res, session] = await Promise.all([
        getPublicBookingConfirmationPage(id),
        manageSurface.createManageSession({ token: id }),
      ]);
      // Retain the view only. A preview neither stores nor uses session tokens.
      if (current === generation.current) setLoaded({ res, view: session.data.view });
    } catch (e) {
      if (current === generation.current) setError(isApiError(e) && e.status === 404
        ? "That booking could not be found. Check the booking ID and try again."
        : "The booking preview could not be loaded. Please try again.");
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  const intl = manageIntl(pageLocale());
  const fmt = timeFormatter(intl.locale);
  const view = loaded?.view;
  const preview = loaded ? {
    ...loaded.res,
    data: { ...loaded.res.data, page: { ...loaded.res.data.page, ...toPutInput(doc), pageId: doc.id } },
  } as PublicConfirmationPageResponse : null;
  const noAction = () => {};
  return (
    <dialog ref={dialog} aria-labelledby="cp-preview-title" onCancel={onClose}
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", maxWidth: "100vw", maxHeight: "100vh", margin: 0, padding: 0, border: 0, background: "#F4F3F9", color: "#14161E", fontFamily: '"DM Sans", "Helvetica Neue", sans-serif' }}>
      <header style={{ padding: 16, background: "white", borderBottom: "1px solid #E0E2E8", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <h1 id="cp-preview-title" style={{ fontSize: 18, margin: 0 }}>Preview: {doc.name}</h1>
          <p style={{ fontSize: 12, margin: "6px 0 0" }}>Current draft with booking data. Nothing is published. Booking actions are disabled in preview.</p>
        </div>
        <button type="button" onClick={onClose} style={{ padding: "8px 12px", border: "1px solid #D1D4DC", borderRadius: 8, background: "white", cursor: "pointer" }}>Back to designer</button>
      </header>
      <form onSubmit={(e) => { e.preventDefault(); void load(); }} style={{ padding: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label htmlFor="cp-preview-booking">Booking ID</label>
        <input id="cp-preview-booking" value={bookingId} disabled={loading} onChange={(e) => setBookingId(e.target.value)} autoComplete="off" style={{ minWidth: 220, padding: 8, border: "1px solid #D1D4DC", borderRadius: 8 }} />
        <button type="submit" disabled={loading} style={{ padding: "8px 12px", border: 0, borderRadius: 8, background: "var(--jrni-color-primary-text, #5142C7)", color: "white", cursor: loading ? "wait" : "pointer" }}>{loading ? "Loading…" : "Load preview"}</button>
      </form>
      {loading ? <p role="status" style={{ padding: "0 16px" }}>Loading booking preview…</p> : null}
      {error ? <p role="alert" style={{ padding: "0 16px" }}>{error}</p> : null}
      {preview && view ? <main data-testid="confirmation-preview" inert style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
        <ConfirmationPageContent res={preview}
          rescheduled={view.status === "rescheduled"}
          customer={{ name: view.customer.display_name, email: view.customer.email }}
          meeting={view.location?.kind === "virtual" ? { virtual: true, joinUrl: view.location.join_url, passcode: view.location.passcode } : undefined}
          answers={[...view.questions].filter((q) => q.rendered_answer.kind !== "information").sort((a, b) => a.position - b.position).map((q) => {
            const value = answerString(q, intl, fmt, view.schedule.timezone);
            return { label: q.label, value: typeof value === "string" ? value : "" };
          }).filter((q) => q.value !== "")}
          wiring={{ policyKnown: true, canMove: view.actions.reschedule.allowed, canCancel: view.actions.cancel.allowed,
            canEditAnswers: view.actions.edit_answers.allowed && view.questions.some((q) => q.editable),
            canPdf: view.actions.summary_pdf.allowed, canHistory: view.actions.rebook_history.allowed,
            dataExport: view.actions.export.allowed, masked: view.masked, canReveal: view.masked && view.actions.verification.kind !== "unavailable",
            onBookAnother: view.actions.book_another.allowed && view.actions.book_another.target_url ? noAction : undefined }} />
      </main> : null}
    </dialog>
  );
}
